import { Component, Input, OnInit } from '@angular/core';
import { MenuDTO } from '../../../system/system-dtos';
import { NodeDetailsDTO } from '../../../common/services/env-configuration/node-details.dto';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { SystemService } from '../../../system/system.service';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { isDefined } from '../../../components/utils/utils';
import { MenuGroupDef } from '../../models/menu-group-def';
import { ActivatedRoute, Router } from '@angular/router';
import { TreeSidenavMenuItemComponent } from './tree-sidenav-menu-item/tree-sidenav-menu-item.component';
import { NAVIGATION_DEFINITION } from '../../models/sub-nav-def';
import { PermissionService } from '../../../core/services/permission.service';

export class MenuNode {
  children?: MenuNode[];
  label: string;
  code?: string;
  subCode?: string;
  menuItem?: MenuDTO;
  subNav?: string;
  level: number;
  active: boolean;
  hasPermission: boolean;
}

@Component({
  selector: 'app-tree-sidenav',
  templateUrl: './tree-sidenav.component.html',
  styleUrls: ['./tree-sidenav.component.scss'],
  standalone: false,
})
export class TreeSidenavComponent implements OnInit {
  @Input() nodeDetailsDTO: NodeDetailsDTO;
  @Input() public version: string;
  @Input() menuGroupDef: MenuGroupDef[];

  nestedTreeControl: NestedTreeControl<MenuNode>;
  nestedDataSource: MatTreeNestedDataSource<MenuNode>;

  constructor(
    private systemService: SystemService,
    private router: Router,
    private route: ActivatedRoute,
    private permissionService: PermissionService
  ) {
    this.nestedTreeControl = new NestedTreeControl<MenuNode>(this._getChildren);
    this.nestedDataSource = new MatTreeNestedDataSource();
  }

  hasNestedChild = (_: number, nodeData: MenuNode) => !nodeData.code;

  private _getChildren = (node: MenuNode) => node.children;

  ngOnInit() {
    this.systemService
      .getSystem()
      .pipe(
        switchMap(() => of(this.buildMenuTree(this.menuGroupDef, 0))),
        switchMap((value) => this.processNodes(value))
      )
      .subscribe((data) => {
        this.nestedDataSource.data = data;
        let activeNodePath = this.getActiveNodePath(data);
        data.forEach((node) => this.nestedTreeControl.expand(node));
        activeNodePath.forEach((node) => this.nestedTreeControl.expand(node));
      });
  }

  private buildMenuTree(definition: MenuGroupDef[], level: number): MenuNode[] {
    let menuNodes: MenuNode[] = [];
    definition.forEach((menuGroup) => {
      const node = new MenuNode();
      node.level = level;
      node.label = menuGroup.label;
      node.code = menuGroup.menuItem;
      node.subCode = menuGroup.subMenuItem;
      node.subNav = menuGroup.subNav;
      if (isDefined(menuGroup.subNav)) {
        node.code = this.getFirstSubNavTabCode(menuGroup.subNav);
      }
      if (isDefined(menuGroup.subGroup)) {
        node.children = this.buildMenuTree(menuGroup.subGroup, level + 1);
      }
      this.permissionService.hasViewPermission(menuGroup.permission).subscribe((value) => (node.hasPermission = value));

      menuNodes.push(node);
    });
    return menuNodes;
  }

  private addMenuItem(menuNode: MenuNode): Observable<MenuNode> {
    return isDefined(menuNode.code)
      ? this.systemService.getMenuConfig(menuNode.code).pipe(map((menuItem) => ({ ...menuNode, menuItem: menuItem })))
      : of(menuNode);
  }

  private processNodes(menuNodes: MenuNode[]): Observable<MenuNode[]> {
    return combineLatest(
      menuNodes.map((node) => (isDefined(node.children) ? this.processNode(node) : this.addMenuItem(node)))
    );
  }

  private processNode(menuNode: MenuNode): Observable<MenuNode> {
    return of(menuNode).pipe(
      switchMap((menuItem: MenuNode) =>
        this.processNodes(menuItem.children).pipe(map((value) => ({ ...menuNode, children: value })))
      )
    );
  }

  isRootLevel(node: MenuNode): boolean {
    return node.level === 0;
  }

  isActive(menuNode: MenuNode): boolean {
    let urlTree = this.router.createUrlTree([TreeSidenavMenuItemComponent.getNavigationUrl(menuNode)], {
      relativeTo: this.route,
    });
    return this.router.isActive(urlTree, {
      fragment: 'ignored',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      paths: 'subset',
    });
  }

  getActiveNodePath(menuNodes: MenuNode[]): MenuNode[] {
    let nodePath: MenuNode[] = [];
    let root = new MenuNode();
    root.children = menuNodes;
    this.findActiveNode(root, nodePath);
    return nodePath;
  }

  findActiveNode(menuNode: MenuNode, nodeTrack: MenuNode[]) {
    if (!isDefined(menuNode)) return false;

    if (this.isActive(menuNode)) {
      nodeTrack.push(menuNode);
      return true;
    }

    if (isDefined(menuNode.children)) {
      menuNode.children.forEach((child) => {
        if (this.findActiveNode(child, nodeTrack)) {
          nodeTrack.push(menuNode);
          return true;
        }
      });
    }

    return false;
  }

  getFirstSubNavTabCode(subNav: string): string {
    return NAVIGATION_DEFINITION.filter((def) => def.code == subNav).flatMap((nav) => nav.tabs)[0]?.code;
  }

  hasPermission(node: MenuNode) {
    return node.hasPermission || node.children.map((value) => value.hasPermission).reduce((acc, val) => acc || val);
  }
}
